import { CheckCircle2 } from 'lucide-react';
import { useUpdateClauseStatus } from '@/hooks/use-clause-status';
import { usePermissions } from '@/hooks/use-permissions';
import type { ClauseStatusDetail } from '@/lib/types';

/**
 * One-click manual attestation. The engine is advisory and never changes a
 * recorded status, so when a clause is "Not assessed" but has evidence and no
 * remaining hints we offer this button; it records Compliant against the
 * current user exactly like the manual status change.
 */
export function MarkCompliantButton({
  clause,
  className = '',
  stopPropagation = false,
}: {
  clause: Pick<ClauseStatusDetail, 'id' | 'clause_ref' | 'can_mark_compliant'>;
  className?: string;
  stopPropagation?: boolean;
}) {
  const updateClause = useUpdateClauseStatus();
  const { hasMinRole } = usePermissions();

  if (!clause.can_mark_compliant || !hasMinRole('Manager')) return null;

  return (
    <button
      type="button"
      disabled={updateClause.isPending}
      onClick={(event) => {
        if (stopPropagation) event.stopPropagation();
        updateClause.mutate({ id: clause.id, data: { status: 'compliant' } });
      }}
      className={`inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50 ${className}`}
      data-testid={`mark-compliant-${clause.id}`}
      title={`Record clause ${clause.clause_ref} as Compliant`}
    >
      <CheckCircle2 className="h-3.5 w-3.5" />
      Mark as compliant
    </button>
  );
}
